export const NEAR_START_SECONDS = 5;
export const NEAR_END_SECONDS = 15;

function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    // Restricted context (SecurityError) — treat as unavailable
    return null;
  }
}

export function shouldClearPlaybackPosition(position: number, duration: number): boolean {
  return Number.isFinite(duration) && duration > 0 && position >= duration - NEAR_END_SECONDS;
}

export function shouldPersistPlaybackPosition(position: number, duration: number): boolean {
  if (!Number.isFinite(position) || position < NEAR_START_SECONDS) {
    return false;
  }

  if (Number.isFinite(duration) && duration > 0 && position >= duration) {
    return false;
  }

  if (shouldClearPlaybackPosition(position, duration)) {
    return false;
  }

  return true;
}

export function readPlaybackPosition(key: string): number | null {
  const storage = getLocalStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(key);
    if (raw == null) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'number' || !Number.isFinite(parsed) || parsed < 0) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function writePlaybackPosition(key: string, seconds: number): void {
  const storage = getLocalStorage();
  if (!storage || !Number.isFinite(seconds) || seconds < 0) {
    return;
  }

  try {
    storage.setItem(key, JSON.stringify(seconds));
  } catch {
    // Private mode / quota — ignore
  }
}

export function clearPlaybackPosition(key: string): void {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}

export const PLAYBACK_SPEED_STORAGE_KEY = 'beehiiv:podcast-playback-speed';
export const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
export const DEFAULT_PLAYBACK_SPEED = 1;

export function readPlaybackSpeed(): number {
  const storage = getLocalStorage();
  if (!storage) {
    return DEFAULT_PLAYBACK_SPEED;
  }

  try {
    const raw = storage.getItem(PLAYBACK_SPEED_STORAGE_KEY);
    if (raw == null) {
      return DEFAULT_PLAYBACK_SPEED;
    }

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'number' || !PLAYBACK_SPEEDS.includes(parsed)) {
      return DEFAULT_PLAYBACK_SPEED;
    }

    return parsed;
  } catch {
    return DEFAULT_PLAYBACK_SPEED;
  }
}

export function writePlaybackSpeed(speed: number): void {
  const storage = getLocalStorage();
  if (!storage || !PLAYBACK_SPEEDS.includes(speed)) {
    return;
  }

  try {
    storage.setItem(PLAYBACK_SPEED_STORAGE_KEY, JSON.stringify(speed));
  } catch {
    // Private mode / quota — ignore
  }
}
